import { useState, useEffect, useMemo, useCallback } from 'react';
import { DollarSign, AlertTriangle, CheckCircle, TrendingDown, Plus, AlertCircle, CreditCard, MailCheck, LayoutList, Columns3, Ban, Download } from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { BulkActionBar } from '@/components/ui/BulkActionBar';
import { KanbanBoard, type KanbanColumn } from '@/components/ui/KanbanBoard';
import { StatsCard } from '@/components/ui/StatsCard';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton, SkeletonStats, SkeletonTable } from '@/components/ui/Skeleton';
import { InvoiceStatusBadge } from '@/components/modules/InvoiceStatusBadge';
import { formatCurrency, formatDate } from '@/lib/format';
import { exportToCSV } from '@/lib/csv';
import { InvoiceSheet } from '@/components/sheets/InvoiceSheet';
import { useFinanceStore } from '@/stores/financeStore';
import { useSelection } from '@/hooks/useSelection';
import { toast } from '@/stores/toastStore';
import type { Invoice } from '@nexus/shared/types';

// ---------------------------------------------------------------------------
// Kanban config
// ---------------------------------------------------------------------------

const KANBAN_COLUMNS: KanbanColumn[] = [
  { id: 'draft', title: 'Draft', color: 'bg-surface-500' },
  { id: 'sent', title: 'Sent', color: 'bg-blue-500' },
  { id: 'viewed', title: 'Viewed', color: 'bg-purple-500' },
  { id: 'overdue', title: 'Overdue', color: 'bg-red-500' },
  { id: 'paid', title: 'Paid', color: 'bg-emerald-500' },
];

type ViewMode = 'table' | 'board';
const VIEW_STORAGE_KEY = 'nexus:finance-view';

function getInitialView(): ViewMode {
  try {
    const stored = localStorage.getItem(VIEW_STORAGE_KEY);
    if (stored === 'table' || stored === 'board') return stored;
  } catch { /* ignore */ }
  return 'table';
}

export function FinanceOps() {
  const {
    invoices,
    total,
    isLoading,
    error,
    fetch: fetchInvoices,
    create: createInvoice,
    update: updateInvoice,
    markPaid,
    followUp,
    bulkUpdate: bulkUpdateInvoices,
    bulkFollowUp: bulkFollowUpInvoices,
    clearError,
  } = useFinanceStore();

  const { selected, toggle, toggleAll, clear: clearSelection, isAllSelected, count: selectionCount } = useSelection(invoices);

  const [viewMode, setViewMode] = useState<ViewMode>(getInitialView);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [createForm, setCreateForm] = useState({
    currency: 'USD',
    dueDate: '',
    lineItems: [{ description: '', quantity: 1, unitPrice: 0 }],
  });

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const totalOutstanding = useMemo(
    () => invoices
      .filter((i) => !['paid', 'void', 'draft'].includes(i.status))
      .reduce((sum, i) => sum + i.amount, 0),
    [invoices],
  );

  const overdue = useMemo(
    () => invoices
      .filter((i) => i.status === 'overdue')
      .reduce((sum, i) => sum + i.amount, 0),
    [invoices],
  );

  const paidTotal = useMemo(
    () => invoices
      .filter((i) => i.status === 'paid')
      .reduce((sum, i) => sum + i.amount, 0),
    [invoices],
  );

  const agingData = useMemo(() => {
    const now = Date.now();
    const buckets = { current: 0, '1-30': 0, '31-60': 0, '61-90': 0, '90+': 0 };
    invoices
      .filter((i) => !['paid', 'void', 'draft'].includes(i.status))
      .forEach((i) => {
        const due = new Date(i.dueDate).getTime();
        const daysOverdue = Math.floor((now - due) / 86_400_000);
        if (daysOverdue <= 0) buckets.current += i.amount;
        else if (daysOverdue <= 30) buckets['1-30'] += i.amount;
        else if (daysOverdue <= 60) buckets['31-60'] += i.amount;
        else if (daysOverdue <= 90) buckets['61-90'] += i.amount;
        else buckets['90+'] += i.amount;
      });
    return [
      { bucket: 'Current', amount: buckets.current },
      { bucket: '1-30 days', amount: buckets['1-30'] },
      { bucket: '31-60 days', amount: buckets['31-60'] },
      { bucket: '61-90 days', amount: buckets['61-90'] },
      { bucket: '90+ days', amount: buckets['90+'] },
    ];
  }, [invoices]);

  const updateLineItem = (index: number, field: string, value: string | number) => {
    setCreateForm((f) => {
      const items = [...f.lineItems];
      items[index] = { ...items[index], [field]: value };
      return { ...f, lineItems: items };
    });
  };

  const addLineItem = () => {
    setCreateForm((f) => ({
      ...f,
      lineItems: [...f.lineItems, { description: '', quantity: 1, unitPrice: 0 }],
    }));
  };

  const removeLineItem = (index: number) => {
    if (createForm.lineItems.length <= 1) return;
    setCreateForm((f) => ({
      ...f,
      lineItems: f.lineItems.filter((_, i) => i !== index),
    }));
  };

  const computedTotal = useMemo(
    () => createForm.lineItems.reduce((sum, li) => sum + li.quantity * li.unitPrice, 0),
    [createForm.lineItems],
  );

  const handleCreate = async () => {
    if (!createForm.dueDate || createForm.lineItems.some((li) => !li.description.trim())) return;
    setCreating(true);
    try {
      const lineItems = createForm.lineItems.map((li) => ({
        description: li.description,
        quantity: li.quantity,
        unitPrice: li.unitPrice,
        total: li.quantity * li.unitPrice,
      }));
      const amount = lineItems.reduce((s, li) => s + li.total, 0);
      await createInvoice({
        amount,
        currency: createForm.currency,
        dueDate: new Date(createForm.dueDate).toISOString(),
        lineItems,
      });
      toast.success('Invoice created');
      setCreateOpen(false);
      setCreateForm({ currency: 'USD', dueDate: '', lineItems: [{ description: '', quantity: 1, unitPrice: 0 }] });
    } catch {
      // error set in store
    } finally {
      setCreating(false);
    }
  };

  const handleMarkPaid = async (id: string) => {
    setActionLoading(id);
    try {
      await markPaid(id);
      toast.success('Marked as paid');
    } catch {
      // error set in store
    } finally {
      setActionLoading(null);
    }
  };

  const handleFollowUp = async (id: string) => {
    setActionLoading(id);
    try {
      await followUp(id);
      toast.success('Follow-up sent');
    } catch {
      // error set in store
    } finally {
      setActionLoading(null);
    }
  };

  const handleBulkFollowUp = async () => {
    if (selected.size === 0) return;
    setBulkLoading(true);
    // Filter to only eligible invoices (not paid, void, or draft)
    const eligibleIds = Array.from(selected).filter((id) => {
      const inv = invoices.find((i) => i.id === id);
      return inv && !['paid', 'void', 'draft'].includes(inv.status);
    });
    if (eligibleIds.length === 0) {
      toast.info('No eligible invoices for follow-up');
      setBulkLoading(false);
      return;
    }
    try {
      await bulkFollowUpInvoices(eligibleIds);
      clearSelection();
    } catch {
      // handled by store
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkVoid = async () => {
    if (selected.size === 0) return;
    setBulkLoading(true);
    try {
      await bulkUpdateInvoices(Array.from(selected), { status: 'void' });
      clearSelection();
    } catch {
      // handled by store
    } finally {
      setBulkLoading(false);
    }
  };

  // -- View toggle --------------------------------------------------------
  const toggleView = useCallback((mode: ViewMode) => {
    setViewMode(mode);
    try { localStorage.setItem(VIEW_STORAGE_KEY, mode); } catch { /* ignore */ }
  }, []);

  // -- Kanban helpers -----------------------------------------------------
  const getInvoiceColumnId = useCallback((invoice: Invoice) => {
    // Map statuses not on the board into the closest column
    if (invoice.status === 'partial') return 'sent';
    if (invoice.status === 'void') return 'draft';
    return invoice.status;
  }, []);

  const handleKanbanMove = useCallback(
    (itemId: string, newColumnId: string) => {
      if (newColumnId === 'paid') {
        markPaid(itemId);
      } else {
        updateInvoice(itemId, { status: newColumnId });
      }
    },
    [markPaid, updateInvoice],
  );

  const renderInvoiceCard = useCallback(
    (invoice: Invoice) => {
      const isOverdue = invoice.status === 'overdue';
      let daysOverdue = 0;
      if (isOverdue) {
        daysOverdue = Math.max(
          0,
          Math.floor((Date.now() - new Date(invoice.dueDate).getTime()) / 86_400_000),
        );
      }

      return (
        <div className="space-y-1.5">
          <p className="text-xs font-mono text-nexus-400">{invoice.invoiceNumber}</p>
          <p className="text-sm font-medium text-surface-100">
            {formatCurrency(invoice.amount, invoice.currency)}
          </p>
          {(invoice.contactId || invoice.companyId) && (
            <p className="text-xs text-surface-400 truncate">
              {invoice.contactId || invoice.companyId}
            </p>
          )}
          {isOverdue && daysOverdue > 0 && (
            <p className="text-[10px] font-semibold text-red-400 bg-red-500/10 px-1.5 py-0.5 rounded inline-block">
              {daysOverdue}d overdue
            </p>
          )}
        </div>
      );
    },
    [],
  );

  const columns: Column<Invoice>[] = [
    {
      key: 'number',
      header: 'Invoice',
      render: (row) => <span className="text-sm font-mono text-nexus-400">{row.invoiceNumber}</span>,
    },
    {
      key: 'amount',
      header: 'Amount',
      sortable: true,
      render: (row) => <span className="text-sm font-medium text-surface-100">{formatCurrency(row.amount, row.currency)}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <InvoiceStatusBadge status={row.status} />,
    },
    {
      key: 'dueDate',
      header: 'Due Date',
      sortable: true,
      render: (row) => <span className="text-xs text-surface-400">{formatDate(row.dueDate)}</span>,
    },
    {
      key: 'followUps',
      header: 'Follow-ups',
      render: (row) => (
        <span className={`text-xs font-medium ${row.followUpCount > 2 ? 'text-red-400' : 'text-surface-400'}`}>
          {row.followUpCount}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-32',
      render: (row) => {
        const loading = actionLoading === row.id;
        if (['paid', 'void', 'draft'].includes(row.status)) return null;
        return (
          <div className="flex items-center gap-1">
            {!['paid', 'void', 'draft'].includes(row.status) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleMarkPaid(row.id)}
                loading={loading}
                icon={<CreditCard className="h-3.5 w-3.5" />}
              >
                Paid
              </Button>
            )}
            {(row.status === 'sent' || row.status === 'overdue' || row.status === 'viewed') && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleFollowUp(row.id)}
                loading={loading}
                icon={<MailCheck className="h-3.5 w-3.5" />}
              >
                Follow Up
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  if (isLoading && invoices.length === 0) {
    return (
      <div className="space-y-6 max-w-[1440px]">
        {/* Header skeleton */}
        <div className="flex items-center justify-between">
          <div>
            <Skeleton className="h-6 w-44 mb-2" />
            <Skeleton className="h-4 w-64" />
          </div>
          <Skeleton className="h-9 w-32 rounded-lg" />
        </div>
        {/* Stats — 4 cards */}
        <SkeletonStats count={4} />
        {/* Main grid: table 2/3 + chart sidebar 1/3 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <SkeletonTable rows={6} cols={6} />
          </div>
          <div className="bg-surface-800 border border-surface-700 rounded-lg p-5">
            <Skeleton className="h-4 w-24 mb-4" />
            <Skeleton className="h-64 w-full rounded-lg" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-[1440px]">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-surface-100">Finance Operations</h2>
          <p className="text-sm text-surface-400 mt-0.5">Invoicing, payments, and accounts receivable</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center bg-surface-800 border border-surface-700 rounded-lg p-0.5">
            <button
              onClick={() => toggleView('table')}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                viewMode === 'table'
                  ? 'bg-surface-700 text-surface-100'
                  : 'text-surface-400 hover:text-surface-300'
              }`}
              title="Table view"
            >
              <LayoutList className="h-3.5 w-3.5" />
              Table
            </button>
            <button
              onClick={() => toggleView('board')}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                viewMode === 'board'
                  ? 'bg-surface-700 text-surface-100'
                  : 'text-surface-400 hover:text-surface-300'
              }`}
              title="Board view"
            >
              <Columns3 className="h-3.5 w-3.5" />
              Board
            </button>
          </div>
          <Button
            variant="secondary"
            size="sm"
            icon={<Download className="h-4 w-4" />}
            onClick={() =>
              exportToCSV(
                invoices,
                [
                  { key: 'invoiceNumber', header: 'Invoice Number' },
                  { key: 'amount', header: 'Amount' },
                  { key: 'currency', header: 'Currency' },
                  { key: 'status', header: 'Status' },
                  { key: 'dueDate', header: 'Due Date' },
                ],
                `nexus-invoices-${new Date().toISOString().slice(0, 10)}.csv`
              )
            }
            disabled={invoices.length === 0}
          >
            Export CSV
          </Button>
          <Button icon={<Plus className="h-4 w-4" />} size="sm" onClick={() => setCreateOpen(true)}>
            New Invoice
          </Button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex items-center gap-2 px-4 py-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <AlertCircle className="h-4 w-4 text-red-400 shrink-0" />
          <p className="text-sm text-red-400">{error}</p>
          <button onClick={clearError} className="ml-auto text-xs text-red-400 hover:text-red-300">Dismiss</button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatsCard label="Total Outstanding" value={formatCurrency(totalOutstanding)} icon={<DollarSign className="h-5 w-5" />} />
        <StatsCard label="Overdue" value={formatCurrency(overdue)} icon={<AlertTriangle className="h-5 w-5" />} />
        <StatsCard label="Paid" value={formatCurrency(paidTotal)} icon={<CheckCircle className="h-5 w-5" />} />
        <StatsCard label="Cash Flow" value={formatCurrency(paidTotal - overdue)} icon={<TrendingDown className="h-5 w-5" />} />
      </div>

      {/* Invoice table / board + AR Aging chart */}
      {viewMode === 'table' ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Invoice table */}
          <div className="lg:col-span-2">
            <div className="bg-surface-800 border border-surface-700 rounded-lg overflow-hidden">
              {invoices.length > 0 ? (
                <DataTable
                  columns={columns}
                  data={invoices}
                  onRowClick={(row) => setSelectedId(row.id)}
                  selectable
                  selectedIds={selected}
                  onToggle={toggle}
                  onToggleAll={toggleAll}
                  isAllSelected={isAllSelected}
                />
              ) : (
                <EmptyState
                  icon={<DollarSign className="h-8 w-8" />}
                  title="No invoices yet"
                  description="Create your first invoice to start tracking payments."
                  action={
                    <Button size="sm" icon={<Plus className="h-4 w-4" />} onClick={() => setCreateOpen(true)}>
                      New Invoice
                    </Button>
                  }
                />
              )}
            </div>
          </div>

          {/* AR Aging chart */}
          <div className="bg-surface-800 border border-surface-700 rounded-lg p-5">
            <h3 className="text-sm font-semibold text-surface-200 mb-4">AR Aging</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={agingData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis
                    type="number"
                    tick={{ fill: '#64748b', fontSize: 11 }}
                    tickLine={false}
                    axisLine={{ stroke: '#334155' }}
                    tickFormatter={(v) => `$${(v / 1000).toFixed(0)}K`}
                  />
                  <YAxis
                    type="category"
                    dataKey="bucket"
                    tick={{ fill: '#64748b', fontSize: 11 }}
                    tickLine={false}
                    axisLine={{ stroke: '#334155' }}
                    width={80}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1e293b',
                      border: '1px solid #334155',
                      borderRadius: '8px',
                      fontSize: '12px',
                      color: '#f1f5f9',
                    }}
                    formatter={(v: number) => formatCurrency(v)}
                  />
                  <Bar dataKey="amount" fill="#10b981" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {invoices.length > 0 ? (
            <KanbanBoard
              columns={KANBAN_COLUMNS}
              items={invoices}
              getColumnId={getInvoiceColumnId}
              renderCard={renderInvoiceCard}
              onMove={handleKanbanMove}
            />
          ) : (
            <div className="bg-surface-800 border border-surface-700 rounded-lg">
              <EmptyState
                icon={<DollarSign className="h-8 w-8" />}
                title="No invoices yet"
                description="Create your first invoice to start tracking payments."
                action={
                  <Button size="sm" icon={<Plus className="h-4 w-4" />} onClick={() => setCreateOpen(true)}>
                    New Invoice
                  </Button>
                }
              />
            </div>
          )}

          {/* AR Aging chart (full width in board mode) */}
          <div className="bg-surface-800 border border-surface-700 rounded-lg p-5">
            <h3 className="text-sm font-semibold text-surface-200 mb-4">AR Aging</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={agingData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis
                    type="number"
                    tick={{ fill: '#64748b', fontSize: 11 }}
                    tickLine={false}
                    axisLine={{ stroke: '#334155' }}
                    tickFormatter={(v) => `$${(v / 1000).toFixed(0)}K`}
                  />
                  <YAxis
                    type="category"
                    dataKey="bucket"
                    tick={{ fill: '#64748b', fontSize: 11 }}
                    tickLine={false}
                    axisLine={{ stroke: '#334155' }}
                    width={80}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1e293b',
                      border: '1px solid #334155',
                      borderRadius: '8px',
                      fontSize: '12px',
                      color: '#f1f5f9',
                    }}
                    formatter={(v: number) => formatCurrency(v)}
                  />
                  <Bar dataKey="amount" fill="#10b981" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Create Invoice Modal */}
      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title="New Invoice" maxWidth="max-w-2xl">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Currency"
              options={[
                { value: 'USD', label: 'USD' },
                { value: 'EUR', label: 'EUR' },
                { value: 'GBP', label: 'GBP' },
              ]}
              value={createForm.currency}
              onChange={(e) => setCreateForm((f) => ({ ...f, currency: e.target.value }))}
            />
            <Input
              label="Due Date"
              type="date"
              value={createForm.dueDate}
              onChange={(e) => setCreateForm((f) => ({ ...f, dueDate: e.target.value }))}
            />
          </div>

          {/* Line items */}
          <div>
            <label className="block text-sm font-medium text-surface-300 mb-2">Line Items</label>
            <div className="space-y-2">
              {createForm.lineItems.map((li, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-5">
                    {i === 0 && <span className="block text-xs text-surface-500 mb-1">Description</span>}
                    <input
                      type="text"
                      className="w-full px-3 py-2 bg-surface-900 border border-surface-700 rounded-lg text-sm text-surface-100 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-nexus-500 focus:border-transparent"
                      placeholder="Service or item"
                      value={li.description}
                      onChange={(e) => updateLineItem(i, 'description', e.target.value)}
                    />
                  </div>
                  <div className="col-span-2">
                    {i === 0 && <span className="block text-xs text-surface-500 mb-1">Qty</span>}
                    <input
                      type="number"
                      min={1}
                      className="w-full px-3 py-2 bg-surface-900 border border-surface-700 rounded-lg text-sm text-surface-100 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-nexus-500 focus:border-transparent"
                      value={li.quantity}
                      onChange={(e) => updateLineItem(i, 'quantity', Math.max(1, parseInt(e.target.value) || 1))}
                    />
                  </div>
                  <div className="col-span-3">
                    {i === 0 && <span className="block text-xs text-surface-500 mb-1">Unit Price</span>}
                    <input
                      type="number"
                      min={0}
                      step={0.01}
                      className="w-full px-3 py-2 bg-surface-900 border border-surface-700 rounded-lg text-sm text-surface-100 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-nexus-500 focus:border-transparent"
                      value={li.unitPrice}
                      onChange={(e) => updateLineItem(i, 'unitPrice', Math.max(0, parseFloat(e.target.value) || 0))}
                    />
                  </div>
                  <div className="col-span-2 flex items-center gap-2">
                    <span className="text-sm text-surface-300 whitespace-nowrap">
                      {formatCurrency(li.quantity * li.unitPrice, createForm.currency)}
                    </span>
                    {createForm.lineItems.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeLineItem(i)}
                        className="text-xs text-red-400 hover:text-red-300"
                      >
                        x
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addLineItem}
              className="mt-2 text-xs text-nexus-400 hover:text-nexus-300 cursor-pointer"
            >
              + Add line item
            </button>
          </div>

          {/* Total */}
          <div className="flex justify-end border-t border-surface-700 pt-3">
            <div className="text-right">
              <span className="text-xs text-surface-500">Total</span>
              <p className="text-lg font-semibold text-surface-100">{formatCurrency(computedTotal, createForm.currency)}</p>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" size="sm" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              icon={<Plus className="h-4 w-4" />}
              onClick={handleCreate}
              loading={creating}
              disabled={!createForm.dueDate || createForm.lineItems.some((li) => !li.description.trim()) || computedTotal <= 0}
            >
              Create Invoice
            </Button>
          </div>
        </div>
      </Modal>

      {/* Invoice Detail Sheet */}
      <InvoiceSheet
        id={selectedId}
        open={!!selectedId}
        onClose={() => setSelectedId(null)}
      />

      {/* Bulk Action Bar */}
      <BulkActionBar count={selectionCount} onClear={clearSelection}>
        <Button
          variant="secondary"
          size="sm"
          onClick={handleBulkFollowUp}
          loading={bulkLoading}
          icon={<MailCheck className="h-3.5 w-3.5" />}
        >
          Follow Up
        </Button>
        <Button
          variant="danger"
          size="sm"
          onClick={handleBulkVoid}
          loading={bulkLoading}
          icon={<Ban className="h-3.5 w-3.5" />}
        >
          Void
        </Button>
      </BulkActionBar>
    </div>
  );
}
